import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalCompleted: 0,
    totalOngoing: 0,
    totalCounties: 0,
    totalLives: 0
  });
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchProjects();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [filter, heroImages.length]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      let params = {};
      if (filter !== 'all') {
        params.filter = filter;
      }
      
      const response = await axios.get(API_ENDPOINTS.PROJECTS, { params });
      if (response.data.success) {
        setProjects(response.data.data);
        // Calculate statistics after projects are loaded
        calculateStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Fallback data for development
      const fallbackProjects = [
        {
          id: 1,
          title: 'Peace Building Initiative in West Pokot',
          description: 'A comprehensive peace building program targeting pastoral communities in West Pokot County',
          content: 'This project focuses on dialogue, mediation, and community-led peace initiatives. We work directly with community leaders, youth groups, and women\'s organizations to establish sustainable peace mechanisms. The program includes conflict resolution training, inter-community dialogue forums, and economic empowerment initiatives to address root causes of conflict.',
          location: 'West Pokot County',
          beneficiaries: '15,000',
          start_date: '2023-01-15',
          end_date: '2024-12-31',
          category: 'ongoing',
          status: 'ongoing',
          thematic_area_name: 'Peace Building',
          slug: 'peace-building-initiative-west-pokot',
          budget: '$500,000',
          donor: 'Various International Donors',
          partners: ['County Government of West Pokot', 'Local CBOs'],
          // NEW: Add statistics fields to match backend
          statistics: [
            { county_name: 'West Pokot', lives_impacted: 15000 }
          ],
          counties_covered: ['West Pokot'],
          total_lives_impacted: 15000
        },
        {
          id: 2,
          title: 'Climate Smart Agriculture Project',
          description: 'Promoting sustainable farming practices among smallholder farmers',
          content: 'Training farmers on climate-resilient agricultural techniques including conservation agriculture, drought-resistant crops, and water harvesting. The project provides farmers with improved seeds, training on sustainable farming practices, and market linkages for their produce.',
          location: 'Turkana County',
          beneficiaries: '8,500',
          start_date: '2022-06-01',
          end_date: '2023-12-31',
          category: 'completed',
          status: 'completed',
          thematic_area_name: 'Sustainable Livelihoods',
          slug: 'climate-smart-agriculture-project',
          budget: '$350,000',
          donor: 'UNDP',
          partners: ['Ministry of Agriculture', 'FAO'],
          // NEW: Add statistics fields to match backend
          statistics: [
            { county_name: 'Turkana', lives_impacted: 8500 }
          ],
          counties_covered: ['Turkana'],
          total_lives_impacted: 8500
        },
        {
          id: 3,
          title: 'Maternal Health Improvement Program',
          description: 'Improving access to maternal healthcare in remote communities',
          content: 'Establishing community health units and training birth attendants. The program focuses on reducing maternal and infant mortality by improving access to prenatal and postnatal care, training traditional birth attendants, and establishing referral systems to health facilities.',
          location: 'Samburu County',
          beneficiaries: '12,000',
          start_date: '2024-03-01',
          end_date: '2025-02-28',
          category: 'ongoing',
          status: 'ongoing',
          thematic_area_name: 'Health & Nutrition',
          slug: 'maternal-health-improvement-program',
          budget: '$420,000',
          donor: 'World Health Organization',
          partners: ['County Health Department', 'Local Hospitals'],
          // NEW: Add statistics fields to match backend
          statistics: [
            { county_name: 'Samburu', lives_impacted: 12000 }
          ],
          counties_covered: ['Samburu'],
          total_lives_impacted: 12000
        },
        {
          id: 4,
          title: 'Youth Empowerment and Skills Training',
          description: 'Vocational training and entrepreneurship for youth',
          content: 'Providing market-relevant skills training to unemployed youth in areas such as carpentry, masonry, tailoring, and ICT. The program includes business development training, startup kits, and mentorship to help youth establish sustainable enterprises.',
          location: 'Baringo County',
          beneficiaries: '3,200',
          start_date: '2023-08-01',
          end_date: '2024-07-31',
          category: 'ongoing',
          status: 'ongoing',
          thematic_area_name: 'Education & Youth',
          slug: 'youth-empowerment-skills-training',
          budget: '$280,000',
          donor: 'EU Development Fund',
          partners: ['TVET Institutions', 'Private Sector'],
          // NEW: Add statistics fields to match backend
          statistics: [
            { county_name: 'Baringo', lives_impacted: 3200 }
          ],
          counties_covered: ['Baringo'],
          total_lives_impacted: 3200
        },
        {
          id: 5,
          title: 'Emergency Food Assistance',
          description: 'Food distribution to drought-affected communities',
          content: 'Providing emergency food aid to vulnerable households affected by prolonged drought. The program includes distribution of food baskets, nutrition supplements for children and pregnant women, and support for community-managed food banks.',
          location: 'Marsabit County',
          beneficiaries: '25,000',
          start_date: '2022-11-01',
          end_date: '2023-04-30',
          category: 'completed',
          status: 'completed',
          thematic_area_name: 'Humanitarian Assistance',
          slug: 'emergency-food-assistance',
          budget: '$750,000',
          donor: 'WFP',
          partners: ['County Government', 'Red Cross'],
          // NEW: Add statistics fields to match backend
          statistics: [
            { county_name: 'Marsabit', lives_impacted: 25000 }
          ],
          counties_covered: ['Marsabit'],
          total_lives_impacted: 25000
        },
        {
          id: 6,
          title: 'Water Harvesting and Conservation',
          description: 'Building water pans and promoting water conservation',
          content: 'Constructing water harvesting structures and training communities on water conservation and management. The project focuses on building sand dams, water pans, and rooftop catchment systems while promoting water-saving agricultural practices.',
          location: 'Kajiado County',
          beneficiaries: '6,800',
          start_date: '2024-01-01',
          end_date: '2024-12-31',
          category: 'ongoing',
          status: 'ongoing',
          thematic_area_name: 'Climate Change',
          slug: 'water-harvesting-conservation',
          budget: '$320,000',
          donor: 'African Development Bank',
          partners: ['Water Resource Authority', 'Community Groups'],
          // NEW: Add statistics fields to match backend
          statistics: [
            { county_name: 'Kajiado', lives_impacted: 6800 }
          ],
          counties_covered: ['Kajiado'],
          total_lives_impacted: 6800
        }
      ];
      
      setProjects(fallbackProjects);
      // Calculate statistics for fallback data
      calculateStatistics(fallbackProjects);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from projects
  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const calculateStatistics = (projectsData) => {
    const completedProjects = projectsData.filter(p => 
      p.category === 'completed' || p.status === 'completed'
    ).length;
    
    const ongoingProjects = projectsData.filter(p => 
      p.category === 'ongoing' || p.status === 'ongoing'
    ).length;
    
    // Calculate unique counties from all projects
    const allCounties = projectsData.reduce((counties, project) => {
      if (project.counties_covered && project.counties_covered.length > 0) {
        // Make sure counties_covered is an array
        const projectCounties = Array.isArray(project.counties_covered) 
          ? project.counties_covered 
          : [];
        return [...new Set([...counties, ...projectCounties])];
      }
      return counties;
    }, []);
    
    // Calculate total lives impacted - handle both old and new field names
    const totalLives = projectsData.reduce((sum, project) => {
      if (project.total_lives_impacted) {
        return sum + (parseInt(project.total_lives_impacted) || 0);
      } else if (project.lives_impacted) {
        return sum + (parseInt(project.lives_impacted) || 0);
      }
      return sum;
    }, 0);
    
    setStatistics({
      totalCompleted: completedProjects,
      totalOngoing: ongoingProjects,
      totalCounties: allCounties.length,
      totalLives: totalLives
    });
  };

  // Helper function to format number with commas
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Helper to display counties for a project
  const renderCounties = (project) => {
    if (!project.counties_covered || project.counties_covered.length === 0) return null;
    
    // Ensure counties_covered is an array
    const counties = Array.isArray(project.counties_covered) 
      ? project.counties_covered 
      : [];
    
    if (counties.length === 0) return null;
    
    return (
      <div className="d-flex flex-wrap gap-1 mt-2">
        {counties.map((county, index) => (
          <span key={index} className="badge bg-light text-dark border">
            {county}
          </span>
        ))}
      </div>
    );
  };

  // Filter projects based on selected filter
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter || project.status === filter);

  const formatDate = (dateString) => {
    if (!dateString) return 'Ongoing';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="projects-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-3 fw-bold text-white mb-4">Our Projects</h1>
              <p className="lead text-white mb-0">
                Implementing sustainable development initiatives across East Africa
              </p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All Projects
                </button>
                <button 
                  className={`btn ${filter === 'ongoing' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('ongoing')}
                >
                  Ongoing Projects
                </button>
                <button 
                  className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('completed')}
                >
                  Completed Projects
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid - UNCHANGED */}
      <section className="section-padding">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted mb-4"></i>
              <h3>No projects found</h3>
              <p className="text-muted">
                {filter !== 'all' ? `No ${filter} projects at the moment.` : 'Check back later for new projects'}
              </p>
              {filter !== 'all' && (
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => setFilter('all')}
                >
                  View All Projects
                </button>
              )}
            </div>
          ) : (
            <div className="row g-4">
              {filteredProjects.map((project) => (
                <div className="col-lg-4 col-md-6" key={project.id}>
                  <div className="card border-0 shadow-sm hover-card h-100">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3">
                        <span className={`badge ${(project.category === 'ongoing' || project.status === 'ongoing') ? 'bg-info' : 'bg-success'}`}>
                          {(project.category === 'ongoing' || project.status === 'ongoing') ? 'Ongoing' : 'Completed'}
                        </span>
                        {project.thematic_area_name && (
                          <span className="badge bg-secondary ms-2">
                            {project.thematic_area_name}
                          </span>
                        )}
                      </div>
                      
                      <h5 className="card-title">{project.title}</h5>
                      <p className="card-text text-muted flex-grow-1">
                        {project.description || (project.content ? project.content.substring(0, 150) + '...' : 'Project details coming soon...')}
                      </p>
                      
                      <div className="project-details mt-4">
                        {project.location && (
                          <div className="d-flex align-items-center mb-2">
                            <i className="bi bi-geo-alt text-primary me-2"></i>
                            <small>{project.location}</small>
                          </div>
                        )}
                        
                        {/* ADDED: Show counties if available */}
                        {renderCounties(project)}
                        
                        {/* ADDED: Show lives impacted if available */}
                        {(project.total_lives_impacted || project.lives_impacted) && (
                          <div className="d-flex align-items-center mb-2 mt-2">
                            <i className="bi bi-heart-fill text-danger me-2"></i>
                            <small>Lives Impacted: {formatNumber(project.total_lives_impacted || project.lives_impacted)}</small>
                          </div>
                        )}
                        
                        {project.beneficiaries && (
                          <div className="d-flex align-items-center mb-2">
                            <i className="bi bi-people text-primary me-2"></i>
                            <small>Beneficiaries: {project.beneficiaries}</small>
                          </div>
                        )}
                        
                        {project.start_date && (
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar text-primary me-2"></i>
                            <small>
                              {formatDate(project.start_date)}
                              {project.end_date && ` - ${formatDate(project.end_date)}`}
                            </small>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4 pt-3 border-top">
                        <Link 
                          to={`/projects/${project.slug || project.id}`}
                          className="btn btn-outline-primary w-100"
                        >
                          View Details <i className="bi bi-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Statistics - UPDATED to use real data */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row text-center">
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-check-circle display-4 text-success mb-3"></i>
                <h2 className="display-4 fw-bold">{statistics.totalCompleted}</h2>
                <h5 className="fw-normal">Projects Completed</h5>
              </div>
            </div>
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-arrow-clockwise display-4 text-info mb-3"></i>
                <h2 className="display-4 fw-bold">{statistics.totalOngoing}</h2>
                <h5 className="fw-normal">Ongoing Projects</h5>
              </div>
            </div>
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-geo-alt display-4 text-primary mb-3"></i>
                <h2 className="display-4 fw-bold">{statistics.totalCounties}</h2>
                <h5 className="fw-normal">Counties Covered</h5>
              </div>
            </div>
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-people display-4 text-warning mb-3"></i>
                <h2 className="display-4 fw-bold">
                  {statistics.totalLives >= 1000000 
                    ? `${(statistics.totalLives / 1000000).toFixed(1)}M+`
                    : formatNumber(statistics.totalLives) + '+'
                  }
                </h2>
                <h5 className="fw-normal">Lives Impacted</h5>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Projects;